import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  type AffiliateCommissionRow,
  formatCommissionAmount,
} from "@/lib/affiliateDashboard";

type StatusKey = AffiliateCommissionRow["status"];
const STATUS_KEYS: StatusKey[] = ["pending", "approved", "paid", "reversed", "forfeited"];

function summarize(rows: AffiliateCommissionRow[]) {
  const byCurrency = new Map<string, Record<StatusKey, number>>();
  for (const r of rows) {
    const cur = (r.currency || "usd").toUpperCase();
    let bucket = byCurrency.get(cur);
    if (!bucket) {
      bucket = { pending: 0, approved: 0, paid: 0, reversed: 0, forfeited: 0 };
      byCurrency.set(cur, bucket);
    }
    bucket[r.status] += Number(r.commission_amount_cents ?? 0);
  }
  return byCurrency;
}

function KpiStrip({
  title,
  rows,
  emphasis,
  emptyHint,
}: {
  title: string;
  rows: AffiliateCommissionRow[];
  emphasis: "primary" | "muted";
  emptyHint: string;
}) {
  const totals = summarize(rows);
  const currencies = Array.from(totals.keys()).sort();
  return (
    <Card className={emphasis === "muted" ? "shadow-soft border-dashed" : "shadow-soft"}>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          {title}
          <span className="text-xs font-normal text-muted-foreground">
            ({rows.length} of last 100 rows)
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {currencies.length === 0 ? (
          <p className="text-xs text-muted-foreground">{emptyHint}</p>
        ) : (
          currencies.map((cur) => {
            const bucket = totals.get(cur)!;
            return (
              <div key={cur} className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                {STATUS_KEYS.map((s) => (
                  <div key={s} className="rounded-md border border-border px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      {s}
                    </div>
                    <div className="font-medium tabular-nums">
                      {formatCommissionAmount(bucket[s], cur)}
                    </div>
                  </div>
                ))}
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

export function AffiliateKpiPanels({ rows }: { rows: AffiliateCommissionRow[] }) {
  const standard = rows.filter((r) => !r.is_super_affiliate);
  const superRows = rows.filter((r) => r.is_super_affiliate);
  return (
    <div className="space-y-4">
      <KpiStrip
        title="Program totals"
        rows={standard}
        emphasis="primary"
        emptyHint="No standard-affiliate commissions in the loaded window."
      />
      {superRows.length > 0 ? (
        <KpiStrip
          title="Super Affiliate activity (excluded from totals)"
          rows={superRows}
          emphasis="muted"
          emptyHint="No Super Affiliate activity in the loaded window."
        />
      ) : null}
    </div>
  );
}
